// components/slides/slidesUi.tsx
'use client';

// Shared UI vocabulary for the Slides module: verdict/status palettes, the
// RDM 10-point guideline board titles, and the small pill components reused
// across the dashboard, editor toolbar, and right bar.
// Palette follows AGENTS.md §3.5.1 (Notion dark-mode status colors).

import React from 'react';
import type {
  SlideDeckDTO,
  SlideElement,
  SlideElementFrame,
  SlidePageDoc,
  SlideReviewVerdict,
  SlidesGuidelineKey,
} from '#types/types';
import { ELEMENT_VERDICT_COLORS, SLIDES_VALIDATION_POINTS } from '#lib/slides/validationPoints';
import { normFrame } from '#lib/slides/resizeFrame';
import { buildBlockArtwork, westfieldLogoTransform, WESTFIELD_LOGO, type BlockArtwork } from '#lib/slides/blockArtwork';
import { standardFontStyle } from '#lib/slides/standardText';

/** Paints stamp artwork (library stamps, validation card) in its own page-point box; `hidden` skips a cell being typed in. */
export const ArtworkShapes = ({ art, hidden }: { art: Pick<BlockArtwork, 'rects' | 'paths' | 'texts'> & { logos?: BlockArtwork['logos'] }; hidden?: string }) => <>
  {art.rects.map((r, i) => <rect key={`r${i}`} x={r.x} y={r.y} width={r.w} height={r.h} fill={r.fill ?? 'none'} stroke={r.stroke} strokeWidth={r.strokeWidth ?? 0} />)}
  {art.paths.map((p, i) => <path key={`p${i}`} d={p.d} fill="none" stroke={p.color} strokeWidth={p.width} />)}
  {art.logos?.map((logo, i) => {
    const t = westfieldLogoTransform(logo);
    return <g key={`l${i}`} transform={`translate(${t.translateX} ${t.translateY}) scale(${t.scale})`} fill={WESTFIELD_LOGO.color}>
      {WESTFIELD_LOGO.paths.map((p, j) => <path key={j} d={p.d} />)}
    </g>;
  })}
  {art.texts.map((t, i) => t.field && t.field === hidden ? null : <text key={`t${i}`} x={t.x} y={t.y} fill={t.color}
    style={{ ...standardFontStyle(t.font), fontSize: t.size }} textLength={t.width || undefined} lengthAdjust="spacingAndGlyphs">{t.text}</text>)}
</>;
export { normFrame } from '#lib/slides/resizeFrame';

export { guidelineLabel } from '#lib/slides/westfieldGuidelines';

/** The RDM 10-point validation board — derived from the single source of truth
 *  (lib/slides/validationPoints) so the sidebar, the agent and the exported card
 *  always show the same axes in the same official order. */
export const REVIEW_POINTS: { id: string; position: number; title: string }[] = SLIDES_VALIDATION_POINTS.map(
  (p) => ({ id: p.id, position: p.position, title: p.en }),
);

export const VERDICT_STYLES: Record<SlideReviewVerdict, { color: string; label: string }> = {
  pass: { color: ELEMENT_VERDICT_COLORS.pass, label: 'Pass' },
  warn: { color: ELEMENT_VERDICT_COLORS.warn, label: 'Warn' },
  fail: { color: ELEMENT_VERDICT_COLORS.fail, label: 'Fail' },
  missing: { color: ELEMENT_VERDICT_COLORS.missing, label: 'Missing' },
  na: { color: ELEMENT_VERDICT_COLORS.na, label: 'N/A' },
};

/** Note marker severity colors (annotation ink, not status pills). */
export const NOTE_SEVERITY_COLORS: Record<'info' | 'warn' | 'fail', string> = {
  fail: '#FF3B30',
  warn: '#FFA344',
  // RDM review blue — kept in sync with the exporter's NOTE_SEVERITY_HEX.info.
  info: '#2B5CAD',
};

/** GREEN of the three-value review code (positive / validated note) — the exact
 *  colour the agent is instructed to use (slidesCommands, knowledge base). */
export const NOTE_POSITIVE_COLOR = '#2E8B57';

/** Element inspector color swatches (annotation ink). */
export const ELEMENT_SWATCHES = ['#FF3B30', '#FFA344', '#529CCA', '#4DAB9A', '#FFFFFF'];

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;

const DECK_STATUS_STYLES: Record<SlideDeckDTO['status'], { color: string; label: string }> = {
  DRAFT: { color: '#979A9B', label: 'Draft' },
  REVIEWING: { color: '#9A6DD7', label: 'Reviewing' },
  REVIEWED: { color: '#4DAB9A', label: 'Reviewed' },
};

/** ~15% bg / ~20% border at the given hex, per the status pill spec. */
export const pillStyle = (color: string): React.CSSProperties => ({
  backgroundColor: `${color}26`,
  borderColor: `${color}33`,
  color,
});

export const DeckStatusPill = ({ status }: { status: SlideDeckDTO['status'] }) => {
  const s = DECK_STATUS_STYLES[status];
  return (
    <span
      className="shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-medium leading-none"
      style={pillStyle(s.color)}
    >
      {s.label}
    </span>
  );
};

export const deckDisplayName = (deck: SlideDeckDTO): string =>
  (deck.sourceFileName ?? '').replace(/\.pdf$/i, '').trim() || 'Untitled deck';

// ---------------------------------------------------------------------------
// Overlay render primitives, shared by the interactive canvas (SlidesCanvas)
// and the static overlay below. They live here once and only once: the module's
// contract is WYSIWYG between the editor, the page thumbnails and the PDF
// export, so a geometry or palette fix has to land on every surface at once.

/** Default annotation ink. */
export const DEFAULT_INK = '#FF3B30';
/** Default ink for authored (non-annotation) text - matches the PDF export default. */
export const TEXT_INK = '#111111';

/** CSS font stacks mirroring the pdf-lib StandardFonts used in export (sans→Helvetica,
 *  serif→Times, mono→Courier), so the canvas metrics track the PDF closely. */
export const TEXT_FONT_STACKS: Record<'sans' | 'serif' | 'mono', string> = {
  sans: 'ui-sans-serif, Helvetica, Arial, sans-serif',
  serif: 'ui-serif, Georgia, "Times New Roman", Times, serif',
  mono: 'ui-monospace, "Courier New", Courier, monospace',
};

/** rotation was stored and validated but NEVER drawn by any renderer, so a
 *  rotated element silently rendered axis-aligned everywhere. Rotate about
 *  the frame centre, matching the exporter. */
export const rotationStyle = (el: SlideElement): React.CSSProperties =>
  typeof el.rotation === 'number' && el.rotation !== 0
    ? { transform: `rotate(${el.rotation}deg)`, transformOrigin: 'center center' }
    : {};

/** The annotation arrow: shaft plus head, drawn in an SVG padded around the
 *  frame so the head is never clipped. */
export const SlideArrow = ({
  frame,
  color,
  strokeWidth,
  scale,
}: {
  frame: SlideElementFrame;
  color: string;
  strokeWidth: number;
  scale: number;
}) => {
  const b = normFrame(frame);
  const pad = 14;
  const w = Math.max(1, b.w * scale) + pad * 2;
  const h = Math.max(1, b.h * scale) + pad * 2;
  const sx = (frame.x - b.x) * scale + pad;
  const sy = (frame.y - b.y) * scale + pad;
  const ex = (frame.x + frame.w - b.x) * scale + pad;
  const ey = (frame.y + frame.h - b.y) * scale + pad;
  const strokePx = Math.max(1.5, strokeWidth * scale);
  const ang = Math.atan2(ey - sy, ex - sx);
  const hl = Math.max(9, strokePx * 3.5);
  const a1 = ang + Math.PI * 0.85;
  const a2 = ang - Math.PI * 0.85;
  return (
    <svg
      width={w}
      height={h}
      className="pointer-events-none absolute"
      style={{ left: b.x * scale - pad, top: b.y * scale - pad }}
    >
      <path
        d={`M ${sx} ${sy} L ${ex} ${ey} M ${ex + Math.cos(a1) * hl} ${ey + Math.sin(a1) * hl} L ${ex} ${ey} L ${ex + Math.cos(a2) * hl} ${ey + Math.sin(a2) * hl}`}
        stroke={color}
        strokeWidth={strokePx}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Read-only overlay renderer (page thumbnails). Mirrors the SlidesCanvas element
// renderers without interaction chrome; coordinates are page points × scale.

/** Static (non-interactive) render of a page's overlay tree, for the page thumbnails. */
export const SlideStaticOverlay = React.memo(function SlideStaticOverlay({
  page,
  scale,
}: {
  page: SlidePageDoc;
  scale: number;
}) {
  return (
    <>
      {page.elements.map((el, paintIndex) => {
        const z = paintIndex + 1;
        // Note markers scale with the page here (unlike the canvas, where
        // zoom-invariant chrome is deliberate) so a thumbnail stays legible.
        const dot = Math.max(3, 16 * scale * 2.2);
        const ink = el.color ?? DEFAULT_INK;
        const rot = rotationStyle(el);
        const frame = el.frame;
        const b = normFrame(frame);
        switch (el.type) {
          case 'note': {
            // Explicit colour wins, severity is the default — same rule as the exporter.
        const c = el.color ?? NOTE_SEVERITY_COLORS[el.severity ?? 'info'];
            return (
              <React.Fragment key={el.id}>
                <div
                  className="pointer-events-none absolute rounded-full"
                  style={{
                    ...rot,
                    zIndex: z,
                    left: frame.x * scale - dot / 2,
                    top: frame.y * scale - dot / 2,
                    width: dot,
                    height: dot,
                    backgroundColor: c,
                    border: `${Math.max(0.5, dot / 8)}px solid rgba(255,255,255,0.92)`,
                    opacity: el.opacity,
                  }}
                />
              </React.Fragment>
            );
          }
          case 'arrow':
            return <SlideArrow key={el.id} frame={frame} color={ink} strokeWidth={el.strokeWidth ?? 2} scale={scale} />;
          case 'rect':
          case 'ellipse': {
            const strokePx = Math.max(1.5, (el.strokeWidth ?? 2) * scale);
            const filled = el.type === 'rect' && el.fill;
            return (
              <div
                key={el.id}
                className="pointer-events-none absolute flex items-center justify-center overflow-hidden"
                style={{
                  ...rot,
                  zIndex: z,
                  borderRadius: el.type === 'ellipse' ? '50%' : '2px',
                  left: b.x * scale,
                  top: b.y * scale,
                  width: b.w * scale,
                  height: b.h * scale,
                  border: `${strokePx}px solid ${ink}`,
                  backgroundColor: filled ? `${ink}1A` : undefined,
                  opacity: el.opacity,
                }}
              >
                {el.text && (
                  <span className="px-0.5 text-center" style={{ color: ink, fontSize: Math.max(3, 12 * scale) }}>
                    {el.text}
                  </span>
                )}
              </div>
            );
          }
          case 'text':
            return (
              <div
                key={el.id}
                className="pointer-events-none absolute overflow-hidden whitespace-pre-wrap break-words"
                style={{
                  ...rot,
                  zIndex: z,
                  left: frame.x * scale,
                  top: frame.y * scale,
                  width: Math.max(1, frame.w * scale),
                  height: Math.max(1, frame.h * scale),
                  fontSize: (el.fontSize ?? 16) * scale,
                  lineHeight:
                    typeof el.lineHeight === 'number' && el.lineHeight > 0 ? el.lineHeight : 1.25,
                  color: el.color ?? TEXT_INK,
                  fontWeight: el.fontWeight === 'bold' ? 700 : 400,
                  fontStyle: el.italic ? 'italic' : 'normal',
                  fontFamily: TEXT_FONT_STACKS[el.fontFamily ?? 'sans'],
                  textAlign: el.align ?? 'left',
                  opacity: el.opacity,
                }}
              >
                {el.text}
              </div>
            );
          case 'stamp': {
            const v = VERDICT_STYLES[el.verdict ?? 'fail'];
            return (
              <div
                key={el.id}
                className="pointer-events-none absolute flex items-center justify-center overflow-hidden rounded-md"
                style={{
                  ...rot,
                  zIndex: z,
                  left: b.x * scale,
                  top: b.y * scale,
                  width: Math.max(1, b.w * scale),
                  height: Math.max(1, b.h * scale),
                  backgroundColor: `${v.color}26`,
                  border: `${Math.max(0.5, 2 * scale)}px solid ${v.color}`,
                  opacity: el.opacity,
                }}
              >
                <span
                  className="whitespace-nowrap font-bold tracking-wide"
                  style={{
                    color: v.color,
                    fontSize: Math.max(3, Math.min(b.h * scale * 0.5, b.w * scale * 0.18)),
                  }}
                >
                  {el.text}
                </span>
              </div>
            );
          }
          case 'image':
            return (
               
              <img
                key={el.id}
                src={el.url}
                alt=""
                draggable={false}
                className="pointer-events-none absolute z-10 select-none"
                style={{
                  left: b.x * scale,
                  top: b.y * scale,
                  width: b.w * scale,
                  height: b.h * scale,
                  objectFit: 'fill',
                  opacity: el.opacity,
                }}
              />
            );
          case 'patch':
            return (
              <div
                key={el.id}
                className="pointer-events-none absolute z-10 flex items-center justify-center overflow-hidden"
                style={{
                  left: b.x * scale,
                  top: b.y * scale,
                  width: b.w * scale,
                  height: b.h * scale,
                  backgroundColor: el.background,
                  opacity: el.opacity,
                }}
              >
                {el.text ? (
                  <div
                    className="whitespace-pre-wrap px-1 text-center"
                    style={{ fontSize: (el.fontSize ?? 14) * scale, lineHeight: 1.25, color: el.color ?? '#111827' }}
                  >
                    {el.text}
                  </div>
                ) : null}
              </div>
            );
          case 'block':
            return (
              <svg key={el.id} className="pointer-events-none absolute" viewBox={`0 0 ${b.w} ${b.h}`}
                style={{ ...rot, zIndex: z, left: b.x * scale, top: b.y * scale, width: b.w * scale, height: b.h * scale, opacity: el.opacity, overflow: 'visible' }}>
                <ArtworkShapes art={buildBlockArtwork(el.block, el.fields, b.w, b.h)} />
              </svg>
            );
          default:
            return null;
        }
      })}
    </>
  );
});
